import { Container, Graphics } from '../../vendor/pixi.min.mjs';

/**
 * Base class for all game entities.
 *
 * Owns a display Container (with a Graphics placeholder by default),
 * an independent hitbox for collision, and a per-frame update hook.
 *
 * Subclasses override _createPlaceholder() (or rebuild visuals themselves)
 * for custom drawing. Sprite visuals are wired through SkinResolver where needed.
 */
export class BaseEntity {
    /** @type {Container} Root display object — add this to the scene */
    display = new Container();

    /** Position in world coordinates */
    x = 0;
    y = 0;

    /**
     * Hitbox relative to (x, y). Collision checks use this, NOT the visual size.
     * @type {{ offsetX: number, offsetY: number, width: number, height: number }}
     */
    hitbox = { offsetX: 0, offsetY: 0, width: 0, height: 0 };

    /** @type {boolean} When false, update() and rendering are skipped */
    active = true;

    /** @type {Graphics|null} Placeholder visual reference (for rebuilding) */
    _graphics = null;

    /**
     * @param {number} x
     * @param {number} y
     * @param {number} hitW — hitbox width
     * @param {number} hitH — hitbox height
     */
    constructor(x, y, hitW, hitH) {
        this.x = x;
        this.y = y;
        this.hitbox.width = hitW;
        this.hitbox.height = hitH;

        this._buildVisual();
        this.syncPosition();
    }

    /**
     * Called every frame. Override in subclasses for movement / AI.
     * @param {number} delta
     */
    update(delta) {
        // override
    }

    /** Sync display container position to logical x/y */
    syncPosition() {
        this.display.x = this.x;
        this.display.y = this.y;
    }

    /**
     * Returns the absolute hitbox rectangle in world coords.
     * Used by CollisionSystem.
     * @returns {{ x: number, y: number, w: number, h: number }}
     */
    getHitRect() {
        return {
            x: this.x + this.hitbox.offsetX,
            y: this.y + this.hitbox.offsetY,
            w: this.hitbox.width,
            h: this.hitbox.height,
        };
    }

    /**
     * Override to draw the placeholder shape (Graphics).
     * @param {Graphics} g
     */
    _createPlaceholder(g) {
        g.rect(0, 0, this.hitbox.width, this.hitbox.height);
        g.fill(0xff00ff);
    }

    /** Clean up display objects */
    destroy() {
        this.display.removeChildren();
        if (this.display.parent) {
            this.display.parent.removeChild(this.display);
        }
        this.active = false;
    }

    // ── private ────────────────────────────────────────

    _buildVisual() {
        this._graphics = new Graphics();
        this._createPlaceholder(this._graphics);
        this.display.addChild(this._graphics);
    }
}
